import { CheckCircle2, Circle, Copy, OctagonAlert, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DEPLOY_COMPONENTS, type DeployComponent } from '@/lib/deploy-components.generated';

/**
 * Guided deploy assurance: "is what is RUNNING built from what we merged?"
 *
 * Deliberately a CHECKLIST, not a live readout. The freshness checker reads git
 * and kubectl, which the cloud instance cannot do — a panel that rendered a
 * confident status it never actually measured would repeat the failure this whole
 * subsystem exists to end. Every status here is one the operator entered after
 * running the command shown, with the output kept as evidence.
 *
 * Presentational only: all state arrives as props, so it renders under
 * renderToStaticMarkup in tests with no DOM, query client or clipboard.
 */

export type StepStatus = 'pending' | 'passed' | 'failed' | 'blocked';

export const ASSURANCE_PAGES = ['components', 'diagnose', 'remediate', 'verify'] as const;
export type AssurancePage = (typeof ASSURANCE_PAGES)[number];

const PAGE_TITLES: Record<AssurancePage, string> = {
  components: 'Components',
  diagnose: 'Diagnose',
  remediate: 'Remediate',
  verify: 'Verify',
};

const PAGE_BLURB: Record<AssurancePage, string> = {
  components: 'Every deployed component, and how checkable each one honestly is.',
  diagnose: 'Run the checker and record what it actually said.',
  remediate: 'Pin the component to the build you intend to run.',
  verify: 'Prove it, from the running system — not from a green status.',
};

/** What the operator recorded for one component. */
export interface ComponentProgress {
  status: StepStatus;
  evidence: string;
  digest?: string;
  sourceSha?: string;
}

export interface DeployAssuranceViewProps {
  open: boolean;
  page: AssurancePage;
  selectedComponent: string | null;
  progress: Record<string, ComponentProgress>;
  components?: readonly DeployComponent[];
  onOpenChange: (open: boolean) => void;
  onPageChange: (page: AssurancePage) => void;
  onSelectComponent: (name: string) => void;
  onStatusChange: (name: string, status: StepStatus) => void;
  onEvidenceChange: (name: string, evidence: string) => void;
  onFieldChange: (name: string, field: 'digest' | 'sourceSha', value: string) => void;
  onCopy: (value: string) => void;
}

function StatusIcon({ status }: { status: StepStatus }): React.ReactElement {
  if (status === 'passed') return <CheckCircle2 className="h-4 w-4 text-success" />;
  if (status === 'failed') return <XCircle className="h-4 w-4 text-error" />;
  if (status === 'blocked') return <OctagonAlert className="h-4 w-4 text-warning" />;
  return <Circle className="h-4 w-4 text-text-tertiary" />;
}

function statusBadgeClass(status: StepStatus): string {
  if (status === 'passed') return 'border-success/40 text-success';
  if (status === 'failed') return 'border-error/40 text-error';
  if (status === 'blocked') return 'border-warning/40 text-warning';
  return 'border-border text-text-secondary';
}

/** What the check mode means for the operator, in one line. */
export function checkModeHint(component: DeployComponent): string {
  switch (component.check) {
    case 'git-pin':
      return 'Decidable offline: compares the pinned build against its source.';
    case 'release-pin':
      return 'Compares the pinned version against the newest release tag.';
    case 'live-probe':
      return 'Only answerable on the host — nothing in git records what runs.';
    case 'auto':
      return 'Redeploys on every push; drift is structurally impossible.';
  }
}

/**
 * The exact command to run for this component. Generated from the registry so it
 * cannot drift from what the CLI actually accepts.
 */
export function diagnoseCommand(component: DeployComponent): string {
  return component.hasWorkloadProbe
    ? 'bun run deploy:freshness --mode live'
    : 'bun run deploy:freshness';
}

export function remediateCommand(
  component: DeployComponent,
  progress: ComponentProgress | undefined
): string {
  if (component.pinStyle === 'digest') {
    const digest = progress?.digest?.trim() || '<sha256:…>';
    const sha = progress?.sourceSha?.trim() || '<40-hex source sha>';
    return `bun run deploy:pin ${component.name} \\\n  --digest ${digest} \\\n  --source-sha ${sha}`;
  }
  // Tag-pinned components are edited in place; there is no pin command that is
  // honest about a semver bump, and inventing one would be worse than saying so.
  return `# edit ${component.manifestPath ?? '(no manifest)'} — re-pin to the current release`;
}

function CopyRow({
  label,
  command,
  onCopy,
}: {
  label: string;
  command: string;
  onCopy: (value: string) => void;
}): React.ReactElement {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-text-tertiary">{label}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            onCopy(command);
          }}
        >
          <Copy className="h-3.5 w-3.5" />
          <span className="ml-1 text-xs">Copy</span>
        </Button>
      </div>
      <pre className="overflow-x-auto rounded-lg border border-border bg-surface p-3 text-xs text-text-primary">
        {command}
      </pre>
    </div>
  );
}

function ComponentsPage({
  components,
  progress,
  selectedComponent,
  onSelectComponent,
}: Pick<DeployAssuranceViewProps, 'progress' | 'selectedComponent' | 'onSelectComponent'> & {
  components: readonly DeployComponent[];
}): React.ReactElement {
  return (
    <div className="space-y-2">
      {components.map(component => {
        const entry = progress[component.name];
        const status = entry?.status ?? 'pending';
        const held = component.holds.length > 0;
        return (
          <button
            key={component.name}
            type="button"
            onClick={() => {
              onSelectComponent(component.name);
            }}
            className={cn(
              'w-full rounded-xl border p-3 text-left transition-colors',
              component.name === selectedComponent
                ? 'border-primary/50 bg-surface'
                : 'border-border/70 bg-surface/60 hover:border-border'
            )}
          >
            <div className="flex items-center gap-2">
              <StatusIcon status={status} />
              <span className="font-medium text-text-primary">{component.name}</span>
              <Badge variant="outline" className={cn('ml-auto text-xs', statusBadgeClass(status))}>
                {status}
              </Badge>
              <Badge variant="outline" className="text-xs border-border text-text-secondary">
                {component.check}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-text-secondary">{component.description}</p>
            <p className="mt-1 text-xs text-text-tertiary">{checkModeHint(component)}</p>
            {held ? (
              <p className="mt-1 text-xs text-warning">
                held until {component.holds[0].until} — {component.holds[0].reason}
              </p>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function EvidenceBox({
  component,
  progress,
  onStatusChange,
  onEvidenceChange,
}: {
  component: DeployComponent;
  progress: ComponentProgress | undefined;
  onStatusChange: DeployAssuranceViewProps['onStatusChange'];
  onEvidenceChange: DeployAssuranceViewProps['onEvidenceChange'];
}): React.ReactElement {
  const status = progress?.status ?? 'pending';
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-text-tertiary">Result</span>
        {(['passed', 'failed', 'blocked'] as StepStatus[]).map(option => (
          <Button
            key={option}
            variant={status === option ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              onStatusChange(component.name, option);
            }}
          >
            {option}
          </Button>
        ))}
      </div>
      <textarea
        aria-label={`Evidence for ${component.name}`}
        value={progress?.evidence ?? ''}
        onChange={event => {
          onEvidenceChange(component.name, event.target.value);
        }}
        placeholder="Paste the command output here — this is the record that it was actually checked."
        className="h-28 w-full rounded-lg border border-border bg-surface p-3 text-xs text-text-primary"
      />
    </div>
  );
}

/**
 * Everything inside the dialog. Exported separately because Radix renders
 * DialogContent through a portal, which emits NOTHING under
 * renderToStaticMarkup — a test against the wrapper would pass while asserting
 * on an empty string. Tests target this; the wrapper is chrome.
 */
export function DeployAssuranceBody({
  page,
  selectedComponent,
  progress,
  components = DEPLOY_COMPONENTS,
  onPageChange,
  onSelectComponent,
  onStatusChange,
  onEvidenceChange,
  onFieldChange,
  onCopy,
}: Omit<DeployAssuranceViewProps, 'open' | 'onOpenChange'>): React.ReactElement {
  const active = components.find(c => c.name === selectedComponent) ?? null;
  const pageIndex = ASSURANCE_PAGES.indexOf(page);

  return (
    <>
      <nav className="flex items-center gap-1" aria-label="Assurance steps">
        {ASSURANCE_PAGES.map((name, index) => (
          <Button
            key={name}
            variant={name === page ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              onPageChange(name);
            }}
          >
            <span className="text-xs">
              {index + 1}. {PAGE_TITLES[name]}
            </span>
          </Button>
        ))}
      </nav>

      <div className="max-h-[22rem] overflow-y-auto pr-1">
        {page === 'components' ? (
          <ComponentsPage
            components={components}
            progress={progress}
            selectedComponent={selectedComponent}
            onSelectComponent={onSelectComponent}
          />
        ) : null}

        {page !== 'components' && !active ? (
          <p className="rounded-lg border border-border bg-surface/60 p-4 text-sm text-text-secondary">
            Pick a component on step 1 first — these steps are per component.
          </p>
        ) : null}

        {page === 'diagnose' && active ? (
          <div className="space-y-3">
            <CopyRow label="Run" command={diagnoseCommand(active)} onCopy={onCopy} />
            {!active.hasWorkloadProbe ? (
              <p className="text-xs text-text-tertiary">
                No workload probe for {active.name}: this check can tell you whether the reference
                is current, but not whether it is running.
              </p>
            ) : null}
            <EvidenceBox
              component={active}
              progress={progress[active.name]}
              onStatusChange={onStatusChange}
              onEvidenceChange={onEvidenceChange}
            />
          </div>
        ) : null}

        {page === 'remediate' && active ? (
          <div className="space-y-3">
            {active.pinStyle === 'digest' ? (
              <div className="grid grid-cols-2 gap-2">
                <input
                  aria-label="Digest"
                  value={progress[active.name]?.digest ?? ''}
                  onChange={e => {
                    onFieldChange(active.name, 'digest', e.target.value);
                  }}
                  placeholder="sha256:…"
                  className="rounded-lg border border-border bg-surface p-2 text-xs"
                />
                <input
                  aria-label="Source SHA"
                  value={progress[active.name]?.sourceSha ?? ''}
                  onChange={e => {
                    onFieldChange(active.name, 'sourceSha', e.target.value);
                  }}
                  placeholder="40-hex source sha"
                  className="rounded-lg border border-border bg-surface p-2 text-xs"
                />
              </div>
            ) : null}
            <CopyRow
              label="Then run"
              command={remediateCommand(active, progress[active.name])}
              onCopy={onCopy}
            />
            <p className="text-xs text-text-tertiary">
              Promotion is <span className="text-text-secondary">{active.promotion}</span>.
              {active.promotion.startsWith('argocd')
                ? ' Merging the pin triggers a sync — it is the deploy.'
                : ' This does not deploy by itself.'}
            </p>
          </div>
        ) : null}

        {page === 'verify' && active ? (
          <div className="space-y-3">
            <CopyRow
              label="Verify"
              command={
                active.verify === 'none'
                  ? '# no automated verification for this component'
                  : active.verify
              }
              onCopy={onCopy}
            />
            <p className="text-xs text-text-tertiary">
              A green status is not proof. Check the running system, and confirm you are looking at
              a fresh instance rather than one that predates the change.
            </p>
            <EvidenceBox
              component={active}
              progress={progress[active.name]}
              onStatusChange={onStatusChange}
              onEvidenceChange={onEvidenceChange}
            />
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-between border-t border-border/70 pt-3">
        <Button
          variant="outline"
          size="sm"
          disabled={pageIndex === 0}
          onClick={() => {
            onPageChange(ASSURANCE_PAGES[Math.max(0, pageIndex - 1)]);
          }}
        >
          Back
        </Button>
        <span className="text-xs text-text-tertiary">
          Step {pageIndex + 1} of {ASSURANCE_PAGES.length}
          {active ? ` — ${active.name}` : ''}
        </span>
        <Button
          size="sm"
          disabled={pageIndex === ASSURANCE_PAGES.length - 1}
          onClick={() => {
            onPageChange(ASSURANCE_PAGES[Math.min(ASSURANCE_PAGES.length - 1, pageIndex + 1)]);
          }}
        >
          Next
        </Button>
      </div>
    </>
  );
}

export function DeployAssuranceView({
  open,
  onOpenChange,
  ...body
}: DeployAssuranceViewProps): React.ReactElement {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Deploy assurance</DialogTitle>
          <DialogDescription>{PAGE_BLURB[body.page]}</DialogDescription>
        </DialogHeader>
        <DeployAssuranceBody {...body} />
      </DialogContent>
    </Dialog>
  );
}
